import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { GradeBadge, PaceFlag } from "@/components/Badges";
import { SyncBanner } from "@/components/SyncBanner";
import { AlertTriangle, MapPin, ChevronRight, Share2 } from "lucide-react";
import { whatsappShare, routeSummaryShareText } from "@/lib/whatsapp";
import { useAuth } from "@/context/AuthContext";
import { MyKRA } from "@/pages/admin/AdminWidgets";

export default function TodayRoute() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [looms, setLooms] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/routes/today");
      setLooms(r.data);
    } catch (e) {
      console.warn(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const share = () => {
    const text = routeSummaryShareText({
      lang,
      inspectorName: user?.name || "",
      date: new Date().toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { weekday: "short", day: "numeric", month: "short" }),
      looms,
    });
    whatsappShare(text);
  };

  return (
    <div>
      <SyncBanner onSyncComplete={load} />
      <div className="flex items-center justify-between mb-4 mt-3">
        <div>
          <div className="eyebrow">{t("nav_todays_route")}</div>
          <h1 className="text-2xl md:text-3xl font-bold" style={{ color: "var(--brand-navy)" }}>
            {new Date().toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { weekday: "long", day: "numeric", month: "short" })}
          </h1>
        </div>
        <button
          data-testid="share-route-btn"
          onClick={share}
          className="tap-target px-3 rounded-md border border-[#1B3A5C]/15 text-sm flex items-center gap-1"
          title={lang === "hi" ? "WhatsApp पर भेजें" : "Share to WhatsApp"}
        >
          <Share2 size={14} /> WhatsApp
        </button>
      </div>

      <div className="mb-4"><MyKRA /></div>

      {loading && <div className="text-sm text-[#7C8E9E]">{t("loading")}</div>}

      <div className="space-y-3">
        {looms.map((l) => (
          <Link
            key={l.id}
            to={`/inspector/loom/${l.id}`}
            data-testid={`loom-card-${l.loom_number}`}
            className="hag-surface p-4 flex items-start gap-3 hover:border-[#1B3A5C]/25"
            style={{ transition: "border-color 0.2s ease" }}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-lg" style={{ color: "var(--brand-navy)" }}>{l.loom_number || "—"}</span>
                {l.overdue && (
                  <span data-testid="overdue-chip" className="status-chip pace-red flex items-center gap-1">
                    <AlertTriangle size={12} /> {t("overdue")}
                  </span>
                )}
                <GradeBadge grade={l.last_grade} />
                <PaceFlag flag={l.pace_flag} />
              </div>
              <div className="text-base mt-1">{l.weaver_name}</div>
              <div className="text-xs text-[#7C8E9E] flex items-center gap-1 mt-1">
                <MapPin size={12} /> {l.village || "—"} · {t("zone")} {l.zone || "?"}
              </div>
              {l.po && (
                <div className="text-xs text-[#4A627A] mt-2">
                  <span className="font-semibold">{l.po.po_number}</span>
                  {l.po.eta && <> · {t("eta")} {new Date(l.po.eta).toLocaleDateString()}</>}
                </div>
              )}
            </div>
            <ChevronRight className="text-[#7C8E9E] mt-1" size={18} />
          </Link>
        ))}
        {!loading && looms.length === 0 && (
          <div data-testid="empty-route" className="hag-surface p-8 text-center text-[#7C8E9E]">
            {lang === "hi"
              ? "आज के रूट पर कोई लूम नहीं। कृपया अपने सुपरवाइज़र से रूट सेट कराएँ।"
              : "No looms on today's route yet. Ask your supervisor to plan your route."}
          </div>
        )}
      </div>
    </div>
  );
}
